"use client";

import { useEffect } from "react";
import Head from "next/head";
import { hexToHslTriple, foregroundTripleFor } from "../../utils/color";

// The CSS variables (globals.css) the brand color overrides. Setting them on
// :root (documentElement) wins over the stylesheet in both light and dark, so
// the brand color is consistent across themes.
const BRAND_VARS = ["--primary", "--sidebar-primary", "--ring", "--sidebar-ring"];
const BRAND_FG_VARS = ["--primary-foreground", "--sidebar-primary-foreground"];

/**
 * Applies admin-configured branding coming from GET /api/config:
 * - the browser tab title (from `branding.title`),
 * - the brand/primary color CSS variables (from `branding.primaryColor`).
 *
 * Rendered inside ConfigProvider so it runs on every page (including public
 * ones). The title goes through next/head; the color is imperative since it
 * targets CSS custom properties.
 */
export default function Branding({ config }) {
	const branding = config?.branding ?? {};
	const { title, primaryColor } = branding;

	useEffect(() => {
		const root = document.documentElement;
		const triple = primaryColor ? hexToHslTriple(primaryColor) : null;
		if (!triple) {
			// No (or invalid) brand color — remove overrides so the theme default
			// from globals.css applies again.
			[...BRAND_VARS, ...BRAND_FG_VARS].forEach((v) =>
				root.style.removeProperty(v),
			);
			return;
		}
		const fg = foregroundTripleFor(primaryColor);
		BRAND_VARS.forEach((v) => root.style.setProperty(v, triple));
		BRAND_FG_VARS.forEach((v) => root.style.setProperty(v, fg));
		return () => {
			[...BRAND_VARS, ...BRAND_FG_VARS].forEach((v) =>
				root.style.removeProperty(v),
			);
		};
	}, [primaryColor]);

	return (
		<Head>
			<title>{title || "Neuquen"}</title>
		</Head>
	);
}
